/**
 * The session chat, applicant side: a launcher that appears once the reviewing
 * team has written, and a sheet with the thread and a reply box.
 *
 * Delivery: the flow's own realtime channel carries `chat-message` (ids only),
 * which `subscribeFlow` re-broadcasts in-page as {@link CHAT_SIGNAL}; this
 * re-reads the thread over REST on it. Without realtime it checks every
 * {@link POLL_MS}. A closed launcher reads without marking anything read — only
 * opening the chat tells the team their message was seen.
 *
 * Fail-silent by design: a chat that cannot load simply does not appear; it
 * must never stand between the applicant and their verification.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { type ChatThread, getChatThread, postChatMessage } from './client';
import { CHAT_SIGNAL } from './realtime';

export const POLL_MS = 20_000;

/** Messages from the team the applicant has not opened the chat to see (server-stamped,
 *  so the count survives a reload). */
export function unseen(thread: ChatThread | null): number {
  return (thread?.messages ?? []).filter((m) => m.sender === 'operator' && !m.read_at).length;
}

export function ChatWidget({ token }: { token: string }) {
  const { t } = useTranslation();
  const [thread, setThread] = useState<ChatThread | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const end = useRef<HTMLLIElement>(null);

  const load = useCallback(
    async (markRead: boolean) => {
      try {
        setThread(await getChatThread(token, markRead));
      } catch {
        // Not shown, not retried loudly: the chat is optional to the flow.
      }
    },
    [token],
  );

  useEffect(() => {
    void load(false);
    const onSignal = () => void load(open);
    window.addEventListener(CHAT_SIGNAL, onSignal);
    const timer = window.setInterval(() => void load(open), POLL_MS);
    return () => {
      window.removeEventListener(CHAT_SIGNAL, onSignal);
      window.clearInterval(timer);
    };
  }, [load, open]);

  useEffect(() => {
    if (open) void load(true);
  }, [open, load]);

  useEffect(() => {
    if (open) end.current?.scrollIntoView?.({ block: 'end' });
  }, [open, thread?.messages.length]);

  if (!thread || thread.messages.length === 0) return null;
  const badge = unseen(thread);

  const send = async () => {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    setError(null);
    try {
      setThread(await postChatMessage(token, body));
      setDraft('');
    } catch (e) {
      const code = (e as { code?: string }).code;
      setError(t(code === 'chat_rate_limited' ? 'flow.chat.slowDown' : 'flow.chat.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="chat-launcher"
        data-track="open_chat"
        aria-label={badge > 0 ? t('flow.chat.openUnread', { count: badge }) : t('flow.chat.open')}
        onClick={() => setOpen(true)}
      >
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          <path
            d="M4 5h16v11H8l-4 4z"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
        {badge > 0 ? <span className="chat-launcher__badge">{badge}</span> : null}
      </button>
      {open
        ? createPortal(
            <div className="country-sheet">
              <div
                className="country-sheet__backdrop"
                aria-hidden="true"
                onClick={() => setOpen(false)}
              />
              <div
                className="country-sheet__panel chat-sheet"
                role="dialog"
                aria-modal="true"
                aria-label={t('flow.chat.title')}
              >
                <div className="country-sheet__handle" aria-hidden="true" />
                <div className="country-sheet__header">
                  <h2 className="country-sheet__title">{t('flow.chat.title')}</h2>
                  <button
                    type="button"
                    className="country-sheet__close"
                    aria-label={t('flow.chat.close')}
                    data-track="close_chat"
                    onClick={() => setOpen(false)}
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                      <path
                        d="M6 6l12 12M18 6L6 18"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  </button>
                </div>
                <ul className="chat-thread">
                  {thread.messages.map((m) => (
                    <li
                      key={m.id}
                      className={`chat-bubble chat-bubble--${m.sender === 'applicant' ? 'mine' : 'theirs'}`}
                    >
                      <span className="chat-bubble__who">
                        {m.sender === 'applicant' ? t('flow.chat.you') : t('flow.chat.team')}
                      </span>
                      <p>{m.body}</p>
                      {m.sender === 'applicant' && m.read_at ? (
                        <span className="chat-bubble__seen">{t('flow.chat.seen')}</span>
                      ) : null}
                    </li>
                  ))}
                  <li aria-hidden="true" ref={end} />
                </ul>
                {error ? (
                  <p className="chat-error" role="alert">
                    {error}
                  </p>
                ) : null}
                {thread.can_reply ? (
                  <form
                    className="chat-composer"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void send();
                    }}
                  >
                    <textarea
                      aria-label={t('flow.chat.placeholder')}
                      placeholder={t('flow.chat.placeholder')}
                      rows={1}
                      maxLength={2000}
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                    />
                    <button type="submit" className="btn" disabled={busy || !draft.trim()} data-track="send_message">
                      {t('flow.chat.send')}
                    </button>
                  </form>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
